import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { GRAPH_COLORS } from '../../config/designTokens';
import type { Measurement } from '../../services/data';

const SPARKLINE_HEIGHT = 80;
/** 15 minutes in ms */
const TICK_INTERVAL_MS = 15 * 60 * 1000;

interface SparklineChartProps {
  data: Measurement[];
  alarmLow?: number;
  alarmHigh?: number;
}

interface SparkPoint {
  t: number; // Unix ms
  value: number;
}

function buildTicks(startMs: number, endMs: number): number[] {
  const ticks: number[] = [];
  const first = Math.ceil(startMs / TICK_INTERVAL_MS) * TICK_INTERVAL_MS;
  for (let ts = first; ts <= endMs; ts += TICK_INTERVAL_MS) {
    ticks.push(ts);
  }
  return ticks;
}

export default function SparklineChart({
  data,
  alarmLow,
  alarmHigh,
}: SparklineChartProps) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded"
        style={{
          height: SPARKLINE_HEIGHT,
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
        }}
      >
        <p className="text-xs text-gray-400">{t('chart.sparklineEmpty')}</p>
      </div>
    );
  }

  // API returns DESC (newest first) — reverse for chronological display
  const points: SparkPoint[] = data
    .slice()
    .reverse()
    .map((m) => ({
      t: new Date(m.timestamp).getTime(),
      value: Number(m.value_calibrated),
    }));

  const startMs = points[0].t;
  const endMs = points[points.length - 1].t;
  const xTicks = buildTicks(startMs, endMs);

  const vals = points.map((p) => p.value);
  const allY = [...vals];
  if (alarmLow !== undefined) allY.push(alarmLow);
  if (alarmHigh !== undefined) allY.push(alarmHigh);
  const yMin = Math.min(...allY);
  const yMax = Math.max(...allY);
  const yPad = Math.max((yMax - yMin) * 0.15, 0.5);
  const yDomain: [number, number] = [yMin - yPad, yMax + yPad];

  return (
    <div style={{ minWidth: 0 }}>
      <ResponsiveContainer width="100%" height={SPARKLINE_HEIGHT} debounce={1}>
        <LineChart data={points} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
          <XAxis
            dataKey="t"
            type="number"
            domain={[startMs, endMs]}
            ticks={xTicks}
            tickFormatter={() => ''}
            tick={{ fontSize: 9 }}
            axisLine={false}
            tickLine={{ stroke: GRAPH_COLORS.gridMajor }}
          />
          <YAxis hide domain={yDomain} />

          {alarmHigh !== undefined && (
            <ReferenceLine
              y={alarmHigh}
              stroke={GRAPH_COLORS.alarmThreshold}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}
          {alarmLow !== undefined && (
            <ReferenceLine
              y={alarmLow}
              stroke={GRAPH_COLORS.alarmThreshold}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}

          <Line
            type="monotone"
            dataKey="value"
            stroke={GRAPH_COLORS.curveAdv}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
